import React from 'react';
import { Link } from 'react-router-dom';
import { Edit, Trash2, CheckCircle2, X } from 'lucide-react';
import Button from './Button';
import Badge from './Badge';
import { Task } from '../types/task';

interface TaskCardProps {
  task: Task;
  deleteConfirm: string | null;
  dependencyCount: number;
  onView: (task: Task) => void;
  onEdit: (task: Task) => void;
  onDeleteConfirm: (taskId: string) => void;
  onDeleteCancel: () => void;
  onDelete: (taskId: string) => void;
  getProjectName: (projectId: string) => string;
  getMilestoneName: (milestoneId: string | undefined) => string | null;
  getUserName: (userId: string) => string;
}

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  deleteConfirm,
  dependencyCount,
  onView,
  onEdit,
  onDeleteConfirm,
  onDeleteCancel,
  onDelete,
  getProjectName,
  getMilestoneName,
  getUserName,
}) => {
  return (
    <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-white/20 dark:border-gray-700/50 rounded-xl p-6 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
      {/* Header */}
      <div className="mb-4 pb-4 border-b-2 border-gray-100 dark:border-gray-700">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 m-0 mb-2">
          <Link
            to={`/tasks/${task.id}`}
            className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            {task.title}
          </Link>
        </h3>
        <div className="flex gap-2 flex-wrap">
          <Badge priority={task.priority as any} size="md">
            {task.priority}
          </Badge>
          <Badge status={task.status as any} size="md">
            {task.status.replace('_', ' ')}
          </Badge>
          {/* Dependency count badge */}
          {dependencyCount > 0 && (
            <Badge 
              variant="info" 
              size="md"
              icon="🔗"
            >
              {dependencyCount}
            </Badge>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="text-gray-700 dark:text-gray-300">
        {task.description ? (
          <p className="mb-4 whitespace-pre-wrap text-gray-700 dark:text-gray-300">
            {task.description}
          </p>
        ) : (
          <p className="italic mb-4 text-gray-400 dark:text-gray-500">
            No description provided
          </p>
        )}
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-md text-sm mt-4">
          <p className="mb-2 text-gray-700 dark:text-gray-300">
            <strong>Project:</strong> {getProjectName(task.project_id)}
          </p>
          {task.milestone_id && (
            <p className="mb-2 text-gray-700 dark:text-gray-300">
              <strong>Milestone:</strong> {getMilestoneName(task.milestone_id)}
            </p>
          )}
          {task.assigned_to && (
            <p className="mb-2 text-gray-700 dark:text-gray-300">
              <strong>Assigned to:</strong> {getUserName(task.assigned_to)}
            </p>
          )}
          {task.due_date && (
            <p className="m-0 text-gray-700 dark:text-gray-300">
              <strong>Due:</strong> {new Date(task.due_date).toLocaleDateString()}
            </p>
          )}
        </div>
        <p
          className="text-sm text-gray-600 dark:text-gray-400"
          style={{ marginTop: 'var(--spacing-md)' }}
        >
          Created: {new Date(task.created_at).toLocaleDateString()}
        </p>
      </div>

      {/* Footer Actions */}
      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex gap-2 flex-wrap">
        <Button onClick={() => onView(task)} variant="secondary" size="sm">
          View Details
        </Button>
        <Button onClick={() => onEdit(task)} variant="primary" size="sm" leftIcon={Edit}>
          Edit
        </Button>
        {deleteConfirm === task.id ? (
          <div className="flex gap-2 items-center">
            <span className="text-sm font-semibold text-red-600">
              Confirm delete?
            </span>
            <Button
              onClick={() => onDelete(task.id)}
              variant="danger"
              size="sm"
              leftIcon={CheckCircle2}
            >
              Yes
            </Button>
            <Button
              onClick={onDeleteCancel}
              variant="outline"
              size="sm"
              leftIcon={X}
            >
              No
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => onDeleteConfirm(task.id)}
            variant="danger"
            size="sm"
            leftIcon={Trash2}
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  );
};

export default TaskCard;
